import { useCallback } from "react";

export const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

export function isSupportedImageType(type: string): boolean {
  return SUPPORTED_IMAGE_TYPES.includes(type);
}

interface UseImageDropZoneOptions {
  /** Receives the dropped or pasted image file (typically `useImageIntake`). */
  onFile: (file: File) => void;
  disabled?: boolean;
}

interface DropZoneProps {
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPaste: (e: React.ClipboardEvent) => void;
}

function addHighlight(e: React.DragEvent) {
  const textarea = e.currentTarget.querySelector("textarea");
  textarea?.classList.add(
    "ring-2",
    "ring-cyan-500",
    "bg-cyan-50",
    "border-cyan-500",
  );
}

function removeHighlight(e: React.DragEvent) {
  const textarea = e.currentTarget.querySelector("textarea");
  textarea?.classList.remove(
    "ring-2",
    "ring-cyan-500",
    "bg-cyan-50",
    "border-cyan-500",
  );
}

export function useImageDropZone({
  onFile,
  disabled = false,
}: UseImageDropZoneOptions): DropZoneProps {
  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        addHighlight(e);
      }
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    removeHighlight(e);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeHighlight(e);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0 && isSupportedImageType(files[0].type)) {
        onFile(files[0]);
      }
    },
    [disabled, onFile],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disabled) return;

      const items = Array.from(e.clipboardData.items);
      const imageItem = items.find((item) => isSupportedImageType(item.type));

      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          onFile(file);
        }
      }
    },
    [disabled, onFile],
  );

  return { onDragOver, onDragLeave, onDrop, onPaste };
}
